import {ApolloCurrentQueryResult} from 'apollo-client';
import React, {useEffect, useMemo} from 'react';

import {
  DefaultResourceConfig,
  RunQueue,
  useEntityDefaultResourceConfigsQuery,
  useFetchLaunchAgentsFromProjectQuery,
} from '../../generated/graphql';
import {Analytics} from '../../services/analytics';
import {
  ProjectData,
  ProjectPageQueryData,
} from '../../state/graphql/projectPageQuery';
import * as ViewerHooks from '../../state/viewer/hooks';
import {launchOverview} from '../../util/urls';
import {Banner} from '../Banner';
import * as S from '../Launch.styles';
import RoutedTab from '../RoutedTab';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import RunQueueTab from '../RunQueueTab';
import DefaultResourceConfigTab from './DefaultResourceConfig/DefaultResourceConfigTab';
import LaunchAgentTab from './LaunchAgentTab';
// @todo: Migrate to 'Link' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Link} from './Link';

const ENTITY_LAUNCH_PAGE_TABS = ['queues', 'agents', 'resources'];

interface LaunchProps {
  tab?: string;
  project: ProjectData;
  entityName: string;
  projectLoading: boolean;
  refetchProject: () => Promise<ApolloCurrentQueryResult<ProjectPageQueryData>>;
}

const LaunchPage: React.FC<LaunchProps> = ({
  tab,
  project,
  entityName,
  projectLoading,
  refetchProject,
}) => {
  const projectName = project.name;
  const viewer = ViewerHooks.useViewer();
  const launchAgentsQuery = useFetchLaunchAgentsFromProjectQuery({
    variables: {
      projectName,
      entityName,
    },
    fetchPolicy: 'cache-and-network',
  });
  const defaultResourceConfigData = useEntityDefaultResourceConfigsQuery({
    variables: {entityName},
  });

  useEffect(() => {
    refetchProject();
  }, [refetchProject]);

  useEffect(() => {
    Analytics.track('Project-scoped Launch Page viewed', {
      entityName,
      projectName,
    });
  });

  const defaultResourceConfigs = useMemo(() => {
    return defaultResourceConfigData.data?.entity?.defaultResourceConfigs?.edges.map(
      x => x.node
    ) as DefaultResourceConfig[];
  }, [defaultResourceConfigData]);
  const refetchDefaultResourceConfigs = defaultResourceConfigData.refetch;
  const baseUrl = launchOverview();

  const isTeam = project.entity.isTeam;
  const runQueues: RunQueue[] = [];
  const projectWriteAccess = !project.readOnly;
  const panes = [
    {
      menuItem: 'Launch Queues',
      render: () => (
        <RunQueueTab
          viewer={viewer}
          entityName={entityName}
          projectLoading={projectLoading}
          projectName={projectName}
          isTeam={isTeam}
          runQueues={runQueues}
          defaultResourceConfigs={defaultResourceConfigs}
          launchAgentsQuery={launchAgentsQuery}
          projectWriteAccess={projectWriteAccess}
          refetchProject={refetchProject}
        />
      ),
    },
    {
      menuItem: 'Launch Agents',
      render: () => (
        <LaunchAgentTab
          runQueues={runQueues}
          launchAgentsQuery={launchAgentsQuery}
          projectName={projectName}
          entityName={entityName}
          viewer={viewer}
        />
      ),
    },
    {
      menuItem: 'Resources',
      render: () => (
        <DefaultResourceConfigTab
          entityName={entityName}
          defaultResourceConfigs={defaultResourceConfigs}
          refetchDefaultResourceConfigs={refetchDefaultResourceConfigs}
        />
      ),
    },
  ];
  return (
    <S.LaunchRoutedDiv>
      <Banner variant="warning" style={{marginBottom: '12px'}}>
        <div>This page is deprecated.</div>
        <Link
          to={launchOverview()}
          onClick={() => {
            Analytics.track('Navigate to new Launch Homepage', {
              via: 'Project-scoped Launch Page deprecation banner',
              entityName,
              projectName,
            });
          }}>
          Go to the new Launch Homepage.
        </Link>
      </Banner>
      <RoutedTab
        baseUrl={baseUrl}
        tabSlugs={ENTITY_LAUNCH_PAGE_TABS}
        activeTabSlug={tab}
        panes={panes}
      />
    </S.LaunchRoutedDiv>
  );
};

export default LaunchPage;
